import React, { useEffect, useState, useCallback } from "react";
import LoadingSpinner from "../../../HelperComponents/LoadingSpinner";
import { deleteListItem, getWorkHistory } from "../../../Service/Services";
import { formatPeriod } from "../../../Utility/Utility";
import AddWorkHistory from "./AddEditWorkHistory";
import ConfirmDialog from "../../../HelperComponents/ConfirmDialog";
import MessageTile from "../../../HelperComponents/MessageTile";
import IconCircleButton from "../../../HelperComponents/IconCircleButton";

interface WorkHistoryProps {
  user: any;
}

type WorkItem = {
  ID: number;
  Company?: string | null;
  WorkAddress?: string | null;
  JobTitle?: string | null;
  Employedmonth?: string | null;
  EmployedYear?: string | null;
  UntilMonth?: string | null;
  UntilYear?: string | null;
  Responsibilities?: string | null;
  Supervisor?: string | null;
  PBSPhone?: string | null;
  ContactSupervisor?: boolean | null;
  ReasonForLeaving?: string | null;
};

const WorkHistory = (props: WorkHistoryProps) => {
  const [loading, setLoading] = useState(false);
  const [workHistory, setWorkHistory] = useState<WorkItem[]>([]);
  const [editWorkOpen, setEditWorkOpen] = useState(false);
  const [selectedWork, setSelectedWork] = useState<WorkItem | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<
    "info" | "success" | "error" | "warning"
  >("info");

  const styles = {
    container: {
      fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
      padding: "12px",
    },
    grid: {
      width: "100%",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
      gridGap: "15px",
      alignItems: "start",
    },
    card: {
      backgroundColor: "hsl(var(--background))",
      borderRadius: "12px",
      boxShadow: "0 2px 12px rgba(0, 0, 0, 0.07)",
      padding: "18px",
      border: "1px solid hsl(var(--border))",
    },
    sectionTitle: {
      fontSize: "16px",
      fontWeight: "700",
      color: "hsl(var(--primary))",
      paddingBottom: "8px",
      borderBottom: "1px solid hsl(var(--border))",
      marginBottom: "12px",
    },
    headerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "8px",
    },
    titleGroup: {
      display: "inline-flex",
      alignItems: "center",
      gap: "40px",
    },
    cardActions: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    badge: {
      display: "inline-block",
      fontSize: "12px",
      fontWeight: 600,
      padding: "4px 8px",
      borderRadius: "999px",
      background: "hsl(var(--primary))",
      color: "hsl(var(--foreground))",
      border: "1px solid hsl(var(--border))",
    },
    detailRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      marginBottom: "10px",
    },
    label: {
      fontSize: "13px",
      fontWeight: "600",
      color: "hsl(var(--foreground))",
    },
    value: {
      fontSize: "13px",
      color: "hsl(var(--foreground))",
      textAlign: "left" as const,
    },
    pill: {
      display: "inline-block",
      fontSize: "12px",
      fontWeight: 600,
      padding: "4px 10px",
      borderRadius: "999px",
      background: "#d4edda",
      color: "#107c10",
      border: "1px solid #b7dfc3",
    },
    pillRed: {
      display: "inline-block",
      fontSize: "12px",
      fontWeight: 600,
      padding: "4px 10px",
      borderRadius: "999px",
      background: "#f8d7da",
      color: "#a4262c",
      border: "1px solid #f5c6cb",
    },
  };

  const fetchWorkHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getWorkHistory(props.user.FullName);
      setWorkHistory(response.data.items as WorkItem[]);
    } catch (error) {
      console.error("Error fetching work history:", error);
    } finally {
      setLoading(false);
    }
  }, [props?.user?.FullName]);

  const handleDelete = async (work: WorkItem) => {
    try {
      const res = await deleteListItem(
        work.ID,
        "Work Experience",
        props.user.Email,
      );
      setDeleteOpen(false);
      if (res.status === 204) {
        setMessageType("success");
        setMessage("Work record deleted successfully.");
        await fetchWorkHistory();
        setSelectedWork(null);
        setEditWorkOpen(false);
        return;
      }
      setMessageType("error");
      setMessage("Delete failed. Please try again.");
    } catch (err: any) {
      setMessageType("error");
      setMessage(err?.message || "Error deleting record. Please try again.");
    }
  };

  useEffect(() => {
    if (!props.user) return;
    fetchWorkHistory();
  }, [props.user, fetchWorkHistory]);

  return (
    <div style={styles.container}>
      {message && (
        <MessageTile isVisible={true} message={message} type={messageType} />
      )}
      {loading && <LoadingSpinner />}
      {!loading && workHistory.length === 0 && (
        <div
          style={{
            padding: "16px",
            color: "#605e5c",
            background: "#f7f7f7",
            border: "1px solid #edebe9",
            borderRadius: "8px",
          }}
        >
          No Work History Found.
        </div>
      )}
      <div className="editProfile-grid">
        {workHistory.map((item) => (
          <div key={item.ID} style={styles.card}>
            <div style={styles.headerRow}>
              <div style={styles.titleGroup}>
                <h3 style={styles.sectionTitle}>{item.Company}</h3>
              </div>
              <div style={styles.cardActions}>
                <IconCircleButton
                  label="Edit job"
                  icon="mdi:pencil"
                  onClick={() => {
                    setSelectedWork(item);
                    setEditWorkOpen(true);
                  }}
                />
                <IconCircleButton
                  label="Delete job"
                  icon="mdi:delete"
                  variant="danger"
                  onClick={() => {
                    setSelectedWork(item);
                    setDeleteOpen(true);
                  }}
                />
              </div>
            </div>

            <div style={styles.detailRow}>
              <span style={styles.label}>Job Title</span>
              <span style={styles.value}>{item.JobTitle}</span>
            </div>

            <div style={styles.detailRow}>
              <span style={styles.label}>Period</span>
              <span style={styles.value}>
                {formatPeriod(
                  item.Employedmonth,
                  item.EmployedYear,
                  item.UntilMonth,
                  item.UntilYear,
                )}
              </span>
            </div>
            {item.WorkAddress && (
              <div style={styles.detailRow}>
                <span style={styles.label}>Work Address</span>
                <span style={styles.value}>{item.WorkAddress}</span>
              </div>
            )}
            {item.Supervisor && (
              <div style={styles.detailRow}>
                <span style={styles.label}>Supervisor</span>
                <span style={styles.value}>{item.Supervisor}</span>
              </div>
            )}

            {item.PBSPhone && (
              <div style={styles.detailRow}>
                <span style={styles.label}>Phone</span>
                <span style={styles.value}>{item.PBSPhone}</span>
              </div>
            )}

            {item.ContactSupervisor !== null &&
              item.ContactSupervisor !== undefined && (
                <div style={styles.detailRow}>
                  <span style={styles.label}>Contact Supervisor</span>
                  <span style={styles.value}>
                    {item.ContactSupervisor ? (
                      <span style={styles.pill}>Allowed</span>
                    ) : (
                      <span style={styles.pillRed}>Not Allowed</span>
                    )}
                  </span>
                </div>
              )}

            {item.Responsibilities && (
              <div style={styles.detailRow}>
                <span style={styles.label}>Responsibilities</span>
                <span style={styles.value}>{item.Responsibilities}</span>
              </div>
            )}

            {item.ReasonForLeaving && (
              <div style={styles.detailRow}>
                <span style={styles.label}>Reason for Leaving</span>
                <span style={styles.value}>{item.ReasonForLeaving}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      {editWorkOpen && selectedWork && (
        <AddWorkHistory
          isOpen={editWorkOpen}
          onDismiss={() => {
            setEditWorkOpen(false);
            setSelectedWork(null);
          }}
          mode="update"
          workHistory={selectedWork}
        />
      )}
      {deleteOpen && selectedWork && (
        <ConfirmDialog
          isOpen={deleteOpen}
          title="Delete Work Experience"
          message={`Are you sure you want to delete this work experience record? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={async () => {
            await handleDelete(selectedWork);
            setDeleteOpen(false);
          }}
          onCancel={() => {
            setDeleteOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default WorkHistory;
